import { useNav } from '../../../hooks/useNav'
import { bookService } from '../../../service/BookService'
import {
  showErrorToast,
  showLoadingToast,
  showSuccessToast,
  toastKey,
} from '../../../utils/showToast'
import { bookKeys, MODE } from '../../../data/queryFactories'
import { PATH } from '../../../data/router_path'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import BookForm from '../../../features/book-management/book-list/components/BookForm'
import { uploadAndEditBookDTO } from '../../../features/book-management/book-list/BookDataMapping'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { bookCollectionService } from '../../../service/BookCollectionService'
import { bookBorrowService } from '../../../service/BookBorrowService'
import useGetBookByUUID from '../../../features/book-management/book-list/hooks/useGetBookByUUID'
const EditBookPage = () => {
  const { uuid } = useParams()
  const navigate = useNav()
  const queryClient = useQueryClient()

  const [nameToUuidMap, setNameToUuidMap] = useState(new Map())
  const [existingCollectionUuids, setExistingCollectionUuids] = useState([])
  const [originalBorrowData, setOriginalBorrowData] = useState(null)

  const { data, isLoading, error } = useGetBookByUUID(uuid)

  const buildNameToUuidMap = (collections) =>
    new Map(collections.map((c) => [c.name, c.uuid]))
  const normalizeToUuid = (item, map) => {
    if (!item) return null
    if (typeof item === 'string') return map.get(item) || item
    return item.uuid || item
  }

  const unique = (arr) => Array.from(new Set(arr))

  const hasBorrowDataChanged = (newBorrowData) => {
    if (!originalBorrowData || !newBorrowData) return false

    const newTimeOfBorrow = newBorrowData.timeOfBorrow * 3600
    const newNumOfBorrow = newBorrowData.numOfBorrow

    return (
      originalBorrowData.timeOfBorrow !== newTimeOfBorrow ||
      originalBorrowData.numOfBorrow !== newNumOfBorrow
    )
  }

  useEffect(() => {
    const loadCollections = async () => {
      const res = await bookCollectionService.getAll()
      const list = res?.message?.content || []
      setNameToUuidMap(buildNameToUuidMap(list))
    }
    loadCollections()
  }, [])

  useEffect(() => {
    if (!data || nameToUuidMap.size === 0) return
    const normalized = (data.bookCollections || [])
      .map((c) => normalizeToUuid(c, nameToUuidMap))
      .filter(Boolean)
    setExistingCollectionUuids(unique(normalized))
  }, [data, nameToUuidMap])

  useEffect(() => {
    if (data && data.borrow && data.borrowInfo) {
      setOriginalBorrowData({
        timeOfBorrow: data.borrowInfo.timeOfBorrow,
        numOfBorrow: data.borrowInfo.numOfBorrow,
      })
    }
  }, [data])

  const updateBorrowInfoMutation = useMutation({
    mutationFn: async ({ bookId, numOfBorrow, timeOfBorrow }) => {
      const result = await bookBorrowService.updateInfo({
        bookId,
        numOfBorrow,
        timeOfBorrow: timeOfBorrow,
      })

      if (!result.success) {
        throw new Error(result.message.content)
      }

      return result
    },
    onError: (error) => {
      console.log('Borrow info update error:', error)
      showErrorToast(
        'Có lỗi khi cập nhật thông tin mượn trả. Hãy liên hệ với quản trị viên'
      )
    },
    onSuccess: () => {
      showSuccessToast('Cập nhật thông tin mượn trả thành công')
    },
  })

  const updateBookMutation = useMutation({
    mutationFn: async (data) => {
      const { bookInfo } = data
      const submitData = uploadAndEditBookDTO({
        ...bookInfo,
      })
      const result = await bookService.update({
        uuid: uuid,
        payload: submitData,
      })

      if (!result.success) {
        throw new Error(result.message.content)
      }

      if (
        bookInfo.bookType === 'Sách mượn trả' &&
        hasBorrowDataChanged(bookInfo)
      ) {
        await updateBorrowInfoMutation.mutateAsync({
          bookId: uuid,
          numOfBorrow: bookInfo.numOfBorrow,
          timeOfBorrow: bookInfo.timeOfBorrow,
        })
      }

      if (Array.isArray(bookInfo.bookCollections)) {
        const getDetailBook = await bookService.getByUuid(uuid)
        if (!getDetailBook?.success === true) {
          throw new Error('Lấy dữ liệu sách thất bại!')
        }
        const nameBookCollections =
          getDetailBook?.message?.content?.bookCollections
        const idBookCollection = unique(
          nameBookCollections
            .map((x) => normalizeToUuid(x, nameToUuidMap))
            .filter(Boolean)
        )
        const toRemoveName = idBookCollection.filter(
          (item) => !bookInfo.bookCollections.includes(item)
        )
        const toAddName = bookInfo.bookCollections.filter(
          (item) => !idBookCollection.includes(item)
        )
        const toAddId = unique(
          toAddName
            .map((x) => normalizeToUuid(x, nameToUuidMap))
            .filter(Boolean)
        )
        const toRemoveId = unique(
          toRemoveName
            .map((x) => normalizeToUuid(x, nameToUuidMap))
            .filter(Boolean)
        )
        if (toAddId.length > 0) {
          const assignResult = await bookService.assignCollections({
            book: uuid,
            collections: toAddId,
          })
        }
        if (toRemoveId.length > 0) {
          for (const removedId of toRemoveId) {
            const payload = {
              bookId: uuid,
              collectionId: removedId,
            }
            const removeResult =
              await bookService.removeBookfromBookCollection(payload)
          }
        }
      }

      return { result }
    },
    onMutate: () => showLoadingToast('Đang cập nhật sách...'),
    onError: (error) => {
      console.log('error', error)
      showErrorToast(
        'Có lỗi khi đang cập nhật sách. Hãy liên hệ với quản trị viên'
      )
    },
    onSuccess: () => {
      showSuccessToast('Cập nhật sách thành công')
      queryClient.invalidateQueries({ queryKey: bookKeys.all })
      navigate(PATH.BOOK.LIST)
    },
  })

  if (isLoading) {
    return 'Đang tải'
  }
  if (error || !data) {
    navigate(PATH.BOOK.LIST)
  }

  return (
    <BookForm
      mode={MODE.EDIT}
      initForm={
        data
          ? {
              title: data.title,
              author: data.author,
              publisher: data.publisher,
              categoryUuid: data.categoryUuid,
              bookCollections: data.bookCollections || [],
              publishYear: data.publishYear,
              facultyUuid: data.faculty,
              subject: data.subject,
              isbn: data.isbn,
              price: parseInt(data.price) || 0,
              description: data.description,
              bookType: data.borrow ? 'Sách mượn trả' : 'Sách thường',
              timeOfBorrow: data.borrowInfo?.timeOfBorrow
                ? Math.floor(data.borrowInfo.timeOfBorrow / 3600).toString()
                : null,
              numOfBorrow: data.borrowInfo?.numOfBorrow ?? null,
            }
          : null
      }
      initPdf={null}
      initCover={null}
      initPdfInfo={{
        pages: data.pages,
        size: parseFloat(data.fileSize) || 0,
        readieEnabled: data.readieEnabled,
      }}
      submitHandler={(pdfFile, cover, formData) => {
        updateBookMutation.mutate({
          bookInfo: formData,
        })
      }}
      confirmTitle="Xác nhận cập nhật sách?"
    />
  )
}
export default EditBookPage
